export interface BaseTimelineItem {
  id: number
  recordId: number
  personId: number
  ts: string
  appointment: any | null
  assistant: {
    name: string
    avatar: string
  }
  callDuration: number | null
  content: string | null
  feedbackCounts: any | null
  invitees: any[] | null
  isIncoming: boolean
  itemType: string
  objections: any | null
  outcome: string | null
  reaction: any | null
  itemActionStatus: string | null
  phoneProviderCallId: string | null
  callEvents: any[]
  data: any | null
  intentType: string | null
  recordingUrl: string | null
  recordingFileObjKey: string | null
  conversation: any[] | null
  stepId: number | null
  isActiveConversationItem?: boolean
  delayShortAction: string | null
  [key: string]: any
}

interface TimelineItemOptions {
  id?: number
  recordId?: number
  personId?: number
  content?: string | null
  isIncoming?: boolean
  outcome?: string | null
  itemActionStatus?: string | null
  callDuration?: number | null
  phoneProviderCallId?: string | null
  appointment?: any | null
  stepId?: number | null
  callEvents?: any[]
  data?: any | null
  intentType?: string | null
  recordingUrl?: string | null
  recordingFileObjKey?: string | null
  invitees?: any[] | null
  isActiveConversationItem?: boolean
  delayShortAction?: string | null
  [key: string]: any
}

export class TimelineObjectFactory {
  private static generateId(): number {
    return Math.floor(Math.random() * 1000000) + Date.now()
  }

  private static getBaseObject(options: TimelineItemOptions = {}): BaseTimelineItem {
    return {
      id: options.id || this.generateId(),
      recordId: options.recordId || options.id || this.generateId(),
      personId: options.personId || 4002,
      ts: new Date().toISOString(),
      appointment: options.appointment || null,
      assistant: {
        name: options.assistant?.name || "PLACEHOLDER",
        avatar: options.assistant?.avatar || "PLACEHOLDER"
      },
      callDuration: options.callDuration || null,
      content: options.content || null,
      feedbackCounts: options.feedbackCounts || null,
      invitees: options.invitees || null,
      isIncoming: options.isIncoming || false,
      itemType: options.itemType,
      objections: options.objections || null,
      outcome: options.outcome || null,
      reaction: options.reaction || null,
      itemActionStatus: options.itemActionStatus || null,
      phoneProviderCallId: options.phoneProviderCallId || null,
      callEvents: options.callEvents || [],
      data: options.data || null,
      intentType: options.intentType || null,
      recordingUrl: options.recordingUrl || null,
      recordingFileObjKey: options.recordingFileObjKey || null,
      stepId: options.stepId || null,
      conversation: options.conversation || [],
      timelineSummary: options.timelineSummary || null,
      questions: options.questions || null,
      isActiveConversationItem: options.isActiveConversationItem || false,
      delayShortAction: options.delayShortAction || null,
      ...options
    }
  }

  static createSMS(options: TimelineItemOptions = {}): BaseTimelineItem {
    return {
      ...this.getBaseObject(options),
      itemType: "text",
      content: options.content || null,
      outcome: options.outcome || null
    }
  }

  static createStage(options: TimelineItemOptions = {}): BaseTimelineItem {
    return {
      ...this.getBaseObject(options),
      itemType: "post_analysis",
      content: options.content || null,
      intentType: options.intentType || "stage",
    }
  }

  static createInitialSummary(options: TimelineItemOptions = {}): BaseTimelineItem {
    return {
      ...this.getBaseObject(options),
      itemType: "init_summary",
      content: options.content || null
    }
  }

  static createLeadValidation(options: TimelineItemOptions = {}): BaseTimelineItem {
    return {
      ...this.getBaseObject(options),
      itemType: "data_validation",
      content: options.content || null,
      itemActionStatus: "success",
    }
  }

  static createAppointment(options: TimelineItemOptions = {}): BaseTimelineItem {
    return {
      ...this.getBaseObject(options),
      itemType: "appointment",
      content: options.content || null,
      invitees: options.invitees || [],
      appointment: options.appointment
        ? {
          ...options.appointment,
          status: options.appointment.status || 'scheduled',
          platform: options.appointment.platform || 'Other'
        }
        : null,
    }
  }

  static createCallOrVoicemail(options: TimelineItemOptions = {}): BaseTimelineItem {
    return {
      ...this.getBaseObject(options),
      itemType: options.itemType,
      content: options.content || null,
      data: options.data || null,
      callDuration: options.callDuration || 0,
      phoneProviderCallId: options.phoneProviderCallId || null,
      callEvents: options.callEvents || [],
      conversation: options.conversation || [],
      recordingUrl: options.recordingUrl || null,
      recordingFileObjKey: options.recordingFileObjKey || null,
      itemActionStatus: options.itemActionStatus || null,
      outcome: options.outcome || null
    }
  }

  static createIncomingSMS(options: TimelineItemOptions = {}): BaseTimelineItem {
    return {
      ...this.getBaseObject(options),
      itemType: "text",
      content: options.content || null,
      outcome: options.outcome || null,
      isIncoming: true
    }
  }

  static createNote(options: TimelineItemOptions = {}): BaseTimelineItem {
    return {
      ...this.getBaseObject(options),
      itemType: "note",
      content: options.content || null,
      delayShortAction: options.delayShortAction || null,
      intentType: options.intentType || null
    }
  }

  static createEventItem(options: TimelineItemOptions = {}): BaseTimelineItem {
    return {
      ...this.getBaseObject(options),
      itemType: "event",
      content: options.content || null,
      outcome: options.outcome || null
    }
  }
}